import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import JsBarcode from 'jsbarcode';
import countries from 'i18n-iso-countries';
import enLocale from 'i18n-iso-countries/langs/en.json';
import type { ProductLabelDesign, ProductWorkspaceDetail } from '../productLabelTypes';
import { inchesToPx, mergeLabelLayout } from '../productLabelTypes';
import {
  computeLabelMetrics,
  countVisibleFields,
  gridAreasForTemplate,
  maxCharsForField,
  resolveLayoutTemplate,
  truncateToFit,
  type LabelLayoutTemplate,
  type LabelMetrics,
} from '../labelLayoutEngine';
import type { LabelFieldContext } from '../labelFieldRegistry';
import { LabelFieldOrderStack, type LabelFieldRenderProps } from '../labelFieldBlocks';
import { LabelCanvasSurface, type LabelCanvasRenderContext } from '../labelCanvasRenderer';

countries.registerLocale(enLocale);

type Props = {
  product: Pick<
    ProductWorkspaceDetail,
    | 'sku'
    | 'name'
    | 'description'
    | 'benefits'
    | 'imageUrl'
    | 'unitLabel'
    | 'baseUnit'
    | 'salePrice'
    | 'pricePerUnit'
    | 'hsCode'
    | 'countryOfOrigin'
    | 'typicalExpiryDays'
    | 'category'
    | 'discountPercent'
  >;
  design: Pick<
    ProductLabelDesign,
    | 'widthIn'
    | 'heightIn'
    | 'layout'
    | 'packageWeightLbs'
    | 'packageLengthIn'
    | 'packageWidthIn'
    | 'packageHeightIn'
    | 'packageMaterial'
  >;
  netQuantityLabel?: string | null;
  formatPrice: (n: number) => string;
  currency: string;
  printId?: string;
  className?: string;
};

function AutoFitShell({
  widthPx,
  heightPx,
  enabled,
  children,
}: {
  widthPx: number;
  heightPx: number;
  enabled: boolean;
  children: React.ReactNode;
}) {
  const outerRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);

  useLayoutEffect(() => {
    if (!enabled) {
      setScale(1);
      return;
    }
    const outer = outerRef.current;
    const inner = innerRef.current;
    if (!outer || !inner) return;

    const recalc = () => {
      const sw = outer.clientWidth / Math.max(inner.scrollWidth, 1);
      const sh = outer.clientHeight / Math.max(inner.scrollHeight, 1);
      setScale(Math.min(1, sw, sh));
    };

    recalc();
    const observer = new ResizeObserver(recalc);
    observer.observe(outer);
    observer.observe(inner);
    return () => observer.disconnect();
  }, [enabled, widthPx, heightPx]);

  return (
    <div
      ref={outerRef}
      className="flex h-full w-full items-center justify-center overflow-hidden"
      style={{ width: widthPx, height: heightPx }}
    >
      <div
        ref={innerRef}
        style={{
          transform: enabled && scale < 1 ? `scale(${scale})` : undefined,
          transformOrigin: 'center center',
          width: '100%',
          height: '100%',
          maxWidth: widthPx,
          maxHeight: heightPx,
        }}
      >
        {children}
      </div>
    </div>
  );
}

function LabelBlock({
  metrics,
  title,
  children,
  muted,
  bordered,
  layout,
}: {
  metrics: LabelMetrics;
  title?: string;
  children: React.ReactNode;
  muted?: boolean;
  bordered?: boolean;
  layout: ReturnType<typeof mergeLabelLayout>;
}) {
  return (
    <div
      className="min-h-0 min-w-0 overflow-hidden"
      style={{
        fontSize: metrics.smallFont,
        lineHeight: 1.25,
        padding: metrics.gap * 0.5,
        backgroundColor: muted ? layout.secondaryBackgroundColor : undefined,
        border: bordered ? `1px solid ${layout.panelBorderColor}` : undefined,
        color: muted ? layout.legalPanelTextColor : layout.textColor,
      }}
    >
      {title ? (
        <div
          className="mb-0.5 font-bold uppercase tracking-wide"
          style={{ fontSize: metrics.smallFont * 0.9, opacity: 0.85 }}
        >
          {title}
        </div>
      ) : null}
      {children}
    </div>
  );
}

function AdaptiveLabelContent({
  product,
  layout,
  metrics,
  template,
  sale,
  unit,
  originName,
  packagingText,
  netQuantityLabel,
  barcodeRef,
  currency,
  formatPrice,
}: {
  product: Props['product'];
  layout: ReturnType<typeof mergeLabelLayout>;
  metrics: LabelMetrics;
  template: LabelLayoutTemplate;
  sale: number;
  unit: string;
  originName: string | null;
  packagingText: string | null;
  netQuantityLabel: string | null;
  barcodeRef: React.RefObject<SVGSVGElement | null>;
  currency: string;
  formatPrice: (n: number) => string;
}) {
  const m = metrics;
  const fs = (n: number) => `${n}px`;

  const brandBlock = layout.showBrand ? (
    <div style={{ fontSize: fs(m.titleFont), fontWeight: 700, color: layout.accentColor, lineHeight: 1.1 }}>
      {truncateToFit(layout.brandName, maxCharsForField(m, 'brand'))}
    </div>
  ) : null;

  const nameBlock = layout.showName ? (
    <div style={{ fontSize: fs(m.baseFont * 1.1), fontWeight: 600, lineHeight: 1.15 }}>
      {truncateToFit(product.name, maxCharsForField(m, 'name'))}
    </div>
  ) : null;

  const imageRow = (
    <div className="flex flex-wrap items-center justify-center gap-1">
      {layout.showImage && product.imageUrl ? (
        <img
          src={product.imageUrl}
          alt=""
          style={{ width: m.imageSize, height: m.imageSize, objectFit: 'cover' }}
          className="shrink-0 border"
        />
      ) : null}
      {layout.extraImages.map((img, idx) =>
        img.url ? (
          <img
            key={img.id || idx}
            src={img.url}
            alt={img.label || ''}
            title={img.label}
            style={{ width: m.extraImageSize, height: m.extraImageSize, objectFit: 'contain' }}
            className="shrink-0"
          />
        ) : null,
      )}
    </div>
  );

  const priceBlock =
    layout.showPrice && sale > 0 ? (
      <div style={{ fontSize: fs(m.baseFont), fontWeight: 700, color: layout.accentColor }}>
        {currency} {formatPrice(sale)}
        {layout.showUnit ? ` / ${unit}` : ''}
      </div>
    ) : null;

  const netBlock =
    layout.showNetQuantity && netQuantityLabel ? (
      <div style={{ fontSize: fs(m.smallFont), fontWeight: 600 }}>{netQuantityLabel}</div>
    ) : null;

  const complianceLines: React.ReactNode[] = [];
  if (layout.showCategory) {
    complianceLines.push(
      <div key="cat">
        <span className="font-semibold">Category:</span>{' '}
        {product.category?.name || '—'}
      </div>,
    );
  }
  if (layout.showHsCode) {
    complianceLines.push(
      <div key="hs">
        <span className="font-semibold">HS:</span> {product.hsCode || '—'}
      </div>,
    );
  }
  if (layout.showCountryOfOrigin) {
    complianceLines.push(
      <div key="origin">
        <span className="font-semibold">Origin:</span>{' '}
        {product.countryOfOrigin ? `${originName} (${product.countryOfOrigin})` : '—'}
      </div>,
    );
  }
  if (layout.showExpiry) {
    complianceLines.push(
      <div key="exp">
        <span className="font-semibold">Shelf life:</span>{' '}
        {product.typicalExpiryDays != null
          ? `${Number(product.typicalExpiryDays).toFixed(0)} days`
          : '—'}
      </div>,
    );
  }
  if (layout.showDescription) {
    complianceLines.push(
      <div key="desc">
        <span className="font-semibold">Description:</span>{' '}
        {product.description
          ? truncateToFit(product.description, maxCharsForField(m, 'description'))
          : '—'}
      </div>,
    );
  }
  if (layout.showBenefits && product.benefits) {
    complianceLines.push(
      <div key="ben" style={{ color: layout.accentColor }}>
        {truncateToFit(product.benefits, maxCharsForField(m, 'benefits'))}
      </div>,
    );
  }
  if (layout.showPackaging && packagingText) {
    complianceLines.push(
      <div key="pkg">
        <span className="font-semibold">Pack:</span> {packagingText}
      </div>,
    );
  }

  const complianceBlock = (
    <LabelBlock metrics={m} title="Product info" muted bordered layout={layout}>
      <div className="space-y-0.5">{complianceLines}</div>
    </LabelBlock>
  );

  const companyBlock =
    layout.showCompanyInfo &&
    (layout.companyPhone || layout.companyWebsite || layout.footerText) ? (
      <LabelBlock metrics={m} layout={layout}>
        {layout.companyPhone ? <div>{layout.companyPhone}</div> : null}
        {layout.companyWebsite ? <div>{layout.companyWebsite}</div> : null}
        {layout.footerText ? (
          <div>{truncateToFit(layout.footerText, maxCharsForField(m, 'footer'))}</div>
        ) : null}
      </LabelBlock>
    ) : null;

  const marketingBlock =
    layout.showMarketing && (layout.marketingTagline || layout.marketingHashtag) ? (
      <LabelBlock metrics={m} layout={layout}>
        {layout.marketingTagline ? <div>{layout.marketingTagline}</div> : null}
        {layout.marketingHashtag ? (
          <div style={{ fontWeight: 700, color: layout.accentColor }}>{layout.marketingHashtag}</div>
        ) : null}
      </LabelBlock>
    ) : null;

  const idBlock = (
    <div className="flex min-h-0 flex-col items-center justify-end gap-0.5">
      {layout.showBarcode ? (
        <svg ref={barcodeRef} style={{ maxHeight: m.barcodeHeight, width: '100%' }} />
      ) : null}
      {layout.showSku ? (
        <div style={{ fontSize: fs(m.smallFont), fontFamily: 'monospace' }}>{product.sku}</div>
      ) : null}
      {layout.companyLegalLine ? (
        <div
          className="text-center uppercase"
          style={{ fontSize: fs(m.smallFont * 0.85), opacity: 0.9 }}
        >
          {truncateToFit(layout.companyLegalLine, maxCharsForField(m, 'legal'))}
        </div>
      ) : null}
    </div>
  );

  const gridStyle = (areas?: string): React.CSSProperties => ({
    display: 'grid',
    gap: m.gap,
    padding: m.padding,
    width: '100%',
    height: '100%',
    boxSizing: 'border-box',
    gridTemplate: areas,
  });

  if (template === 'FIELD_ORDER') {
    const fieldCtx: LabelFieldContext = {
      hasProductImage: Boolean(product.imageUrl),
      extraImageCount: layout.extraImages.filter((i) => i.url).length,
      hasPrice: sale > 0,
      hasNetQty: Boolean(netQuantityLabel),
      hasCategory: Boolean(product.category?.name),
      hasDescription: Boolean(product.description),
      hasBenefits: Boolean(product.benefits),
      hasHs: Boolean(product.hsCode),
      hasOrigin: Boolean(product.countryOfOrigin),
      hasExpiry: product.typicalExpiryDays != null,
      hasPackaging: Boolean(packagingText),
      hasCompany: Boolean(layout.companyPhone || layout.companyWebsite || layout.footerText),
      hasMarketing: Boolean(layout.marketingTagline || layout.marketingHashtag),
      hasLegal: Boolean(layout.companyLegalLine),
      hasFooter: Boolean(layout.footerText),
    };
    const stackProps: LabelFieldRenderProps = {
      product,
      layout,
      metrics: m,
      sale,
      unit,
      originName,
      packagingText,
      netQuantityLabel,
      barcodeRef,
      currency,
      formatPrice,
      fieldCtx,
    };
    return <LabelFieldOrderStack props={stackProps} />;
  }

  if (template === 'CIRCULAR') {
    return (
      <div
        className="flex h-full w-full flex-col items-center justify-center text-center"
        style={{ padding: m.padding, gap: m.gap }}
      >
        {brandBlock}
        {imageRow}
        {nameBlock}
        {netBlock}
        {priceBlock}
        {complianceLines.length > 0 ? (
          <div style={{ fontSize: fs(m.smallFont) }} className="w-full space-y-0.5">
            {complianceLines.slice(0, 4)}
          </div>
        ) : null}
        {idBlock}
      </div>
    );
  }

  if (template === 'VERTICAL_STACK') {
    return (
      <div className="flex h-full w-full flex-col" style={{ padding: m.padding, gap: m.gap }}>
        {brandBlock}
        {imageRow}
        {nameBlock}
        {netBlock}
        {priceBlock}
        {complianceLines.length > 0 ? (
          <div className="min-h-0 flex-1 overflow-hidden" style={{ fontSize: fs(m.smallFont) }}>
            {complianceLines}
          </div>
        ) : null}
        {companyBlock}
        {marketingBlock}
        {idBlock}
      </div>
    );
  }

  if (template === 'SQUARE_CENTER') {
    return (
      <div
        className="flex h-full w-full flex-col items-center justify-center text-center"
        style={{ padding: m.padding, gap: m.gap }}
      >
        {brandBlock}
        {imageRow}
        {nameBlock}
        {priceBlock}
        {netBlock}
        {complianceLines.length > 0 ? (
          <div style={{ fontSize: fs(m.smallFont) }} className="w-full">
            {complianceLines.slice(0, 5)}
          </div>
        ) : null}
        {idBlock}
      </div>
    );
  }

  if (template === 'GRID_2X2') {
    return (
      <div style={{ ...gridStyle(), gridTemplateColumns: '1fr 1fr', gridTemplateRows: '1fr 1fr' }}>
        <div className="flex flex-col justify-center">{brandBlock}{nameBlock}</div>
        <div className="flex items-center justify-center">{imageRow}</div>
        <div className="min-h-0 overflow-hidden" style={{ fontSize: fs(m.smallFont) }}>
          {complianceLines}
        </div>
        <div>{priceBlock}{netBlock}{idBlock}</div>
      </div>
    );
  }

  if (template === 'GRID_3X2') {
    return (
      <div
        style={{
          ...gridStyle(),
          gridTemplateColumns: '1fr 1fr 1fr',
          gridTemplateRows: '1fr 1fr',
        }}
      >
        <div>{brandBlock}</div>
        <div className="col-span-1 flex items-center justify-center">{imageRow}</div>
        <div>{priceBlock}</div>
        <div className="col-span-2 min-h-0 overflow-hidden" style={{ fontSize: fs(m.smallFont) }}>
          {complianceLines}
        </div>
        <div>{idBlock}</div>
      </div>
    );
  }

  if (template === 'COMPLIANCE_GRID') {
    return (
      <div style={gridStyle(gridAreasForTemplate(template))}>
        <div style={{ gridArea: 'compliance' }}>{complianceBlock}</div>
        <div style={{ gridArea: 'details' }} className="min-h-0 overflow-hidden">
          {nameBlock}
          {priceBlock}
          {netBlock}
          {companyBlock}
        </div>
        <div style={{ gridArea: 'meta' }}>{idBlock}{marketingBlock}</div>
      </div>
    );
  }

  if (template === 'BRAND_LEFT') {
    return (
      <div style={gridStyle(gridAreasForTemplate(template))}>
        <div
          style={{
            gridArea: 'brand',
            backgroundColor: layout.accentColor,
            color: layout.backgroundColor,
            padding: m.gap,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          }}
        >
          {layout.showBrand ? (
            <div style={{ fontSize: fs(m.titleFont), fontWeight: 700 }}>{layout.brandName}</div>
          ) : null}
          {layout.showImage && product.imageUrl ? (
            <img
              src={product.imageUrl}
              alt=""
              className="mt-1"
              style={{ width: m.imageSize * 0.9, height: m.imageSize * 0.9, objectFit: 'cover' }}
            />
          ) : null}
        </div>
        <div
          style={{ gridArea: 'content' }}
          className="flex min-h-0 flex-col overflow-hidden"
        >
          {nameBlock}
          {priceBlock}
          {netBlock}
          <div className="min-h-0 flex-1 overflow-hidden" style={{ fontSize: fs(m.smallFont) }}>
            {complianceLines}
          </div>
          {idBlock}
        </div>
      </div>
    );
  }

  // HORIZONTAL_SPLIT (default)
  return (
    <div style={gridStyle(gridAreasForTemplate('HORIZONTAL_SPLIT'))}>
      <div
        style={{ gridArea: 'brand' }}
        className="flex min-h-0 flex-col justify-center overflow-hidden"
      >
        {brandBlock}
        {imageRow}
        {netBlock}
        {priceBlock}
      </div>
      <div style={{ gridArea: 'info' }} className="flex min-h-0 flex-col overflow-hidden">
        {nameBlock}
        <div className="min-h-0 flex-1 overflow-hidden" style={{ fontSize: fs(m.smallFont) }}>
          {complianceLines}
        </div>
        {companyBlock}
        {marketingBlock}
        {idBlock}
      </div>
    </div>
  );
}

export default function ProductLabelPreview({
  product,
  design,
  formatPrice,
  currency,
  netQuantityLabel,
  printId = 'product-label-print-root',
  className = '',
}: Props) {
  const barcodeRef = useRef<SVGSVGElement | null>(null);
  const layout = mergeLabelLayout(design.layout);
  const template = resolveLayoutTemplate(layout);
  const widthPx = inchesToPx(design.widthIn);
  const heightPx = inchesToPx(design.heightIn);
  const isPortrait = layout.orientation === 'portrait';
  const labelW = isPortrait ? heightPx : widthPx;
  const labelH = isPortrait ? widthPx : heightPx;

  const sale = Number(product.salePrice ?? product.pricePerUnit ?? 0);
  const unit = product.unitLabel || product.baseUnit;
  const originName = product.countryOfOrigin
    ? countries.getName(product.countryOfOrigin.toUpperCase(), 'en') || product.countryOfOrigin
    : null;
  const packagingParts = [
    design.packageMaterial,
    design.packageWeightLbs != null ? `${design.packageWeightLbs} lb` : null,
    design.packageLengthIn != null &&
    design.packageWidthIn != null &&
    design.packageHeightIn != null
      ? `${design.packageLengthIn}×${design.packageWidthIn}×${design.packageHeightIn} in`
      : null,
  ].filter(Boolean) as string[];
  const packagingText = packagingParts.length > 0 ? packagingParts.join(' · ') : null;

  const extraCount = layout.extraImages.filter((i) => i.url).length;
  const fieldCount = countVisibleFields(layout, Boolean(product.imageUrl), extraCount, {
    hasPrice: sale > 0,
    hasNetQty: Boolean(netQuantityLabel),
    hasCategory: Boolean(product.category?.name),
    hasDescription: Boolean(product.description),
    hasBenefits: Boolean(product.benefits),
    hasHs: Boolean(product.hsCode),
    hasOrigin: Boolean(product.countryOfOrigin),
    hasExpiry: product.typicalExpiryDays != null,
    hasPackaging: Boolean(packagingText),
    hasCompany: Boolean(layout.companyPhone || layout.companyWebsite || layout.footerText),
    hasMarketing: Boolean(layout.marketingTagline || layout.marketingHashtag),
    hasLegal: Boolean(layout.companyLegalLine),
    hasFooter: Boolean(layout.footerText),
  });

  const metrics = computeLabelMetrics(labelW, labelH, template, fieldCount, layout.fontScale);
  const borderRadius =
    template === 'CIRCULAR' ? '50%' : template === 'CANVAS' ? '0' : '2px';

  const canvasCtx: LabelCanvasRenderContext = {
    product,
    layout,
    design,
    netQuantityLabel,
    formatPrice,
    currency,
    labelW,
    labelH,
  };

  useEffect(() => {
    if (template === 'CANVAS') return;
    if (!layout.showBarcode || !barcodeRef.current || !product.sku) return;
    try {
      JsBarcode(barcodeRef.current, product.sku, {
        format: 'CODE128',
        displayValue: labelW > 120,
        fontSize: Math.max(6, metrics.smallFont),
        height: metrics.barcodeHeight,
        margin: 0,
        lineColor: layout.textColor,
        width: labelW < 100 ? 1 : 1.4,
      });
    } catch {
      // skip invalid barcode
    }
  }, [
    layout.showBarcode,
    layout.textColor,
    product.sku,
    metrics.barcodeHeight,
    metrics.smallFont,
    labelW,
    template,
  ]);

  if (template === 'CANVAS') {
    return (
      <div className={className}>
        <LabelCanvasSurface ctx={canvasCtx} printId={printId} />
      </div>
    );
  }

  return (
    <div className={className}>
      <div
        id={printId}
        data-label-export="true"
        className="product-label-print-target overflow-hidden"
        style={{
          width: labelW,
          height: labelH,
          backgroundColor: layout.backgroundColor,
          color: layout.textColor,
          fontFamily: 'system-ui, -apple-system, "Segoe UI", sans-serif',
          border: `1px solid ${layout.panelBorderColor}`,
          borderRadius,
          boxSizing: 'border-box',
        }}
      >
        <AutoFitShell widthPx={labelW} heightPx={labelH} enabled={layout.autoFit}>
          <AdaptiveLabelContent
            product={product}
            layout={layout}
            metrics={metrics}
            template={template}
            sale={sale}
            unit={unit}
            originName={originName}
            packagingText={packagingText}
            netQuantityLabel={netQuantityLabel ?? null}
            barcodeRef={barcodeRef}
            currency={currency}
            formatPrice={formatPrice}
          />
        </AutoFitShell>
      </div>
    </div>
  );
}
